import React, {
  forwardRef,
  Ref,
  useCallback,
  useEffect,
  memo,
  RefObject,
  ComponentProps,
  FocusEvent,
} from 'react'
import ReactDatePicker, { ReactDatePickerProps } from 'react-datepicker'
import { isIOS } from 'react-device-detect'

import styled from '@emotion/styled'
import { shift, flip } from '@floating-ui/react-dom'
import moment from 'moment-timezone'

import {
  PRIMARY_DATE_FORMAT_DATEFNS,
  convertFromLocalDateRepresentation,
  convertToLocalDateRepresentation,
  convertDateToTimezone,
  useDateTimeContext,
} from '@surglogs/datetime'

import { useHandler, useMemoizedCall } from '@surglogs-app/hooks'
import { Box, Input } from '@surglogs-app/ui'

import 'react-datepicker/dist/react-datepicker.css'

import {
  DatePickerPortalStyles,
  DATEPICKER_PORTAL_ID,
} from './DatePickerPortalStyles'

const convertToTimezone = (
  date: Date | string | moment.Moment | undefined | null,
  timezone: string,
) => convertDateToTimezone(date as any, timezone)?.toDate()

/**
 * Returns string value as is if can't be parsed
 */
const valueAsIsIfInvalid = (
  date: Date | string | moment.Moment | undefined | null,
): string | undefined => {
  if (typeof date === 'string' && !moment(date).isValid()) {
    return date
  }

  return undefined
}

const DateInputContainer = styled(Box, {
  shouldForwardProp: (prop) => !['fullWidth'].includes(prop),
})<{ fullWidth: boolean }>`
  display: flex;
  max-width: ${({ fullWidth }) => (fullWidth ? '100%' : '150px')};

  & .react-datepicker-wrapper {
    width: 100%;
  }

  @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
    flex: 0 0 300px;
    & .react-datepicker__input-container {
      display: block;
    }
  }
`

const getOrCreatePortalElement = (portalId: string) => {
  let element = document.getElementById(portalId)

  if (!element) {
    element = document.createElement('div')
    element.id = portalId
    document.body.appendChild(element)
  }

  return element
}

const portalTrapRefCounts = new Map<string, number>()
const stopMouseDownPropagation = (e: MouseEvent) => e.stopPropagation()

const usePortalMouseDownTrap = (portalId: string | undefined) => {
  useEffect(() => {
    if (!portalId) {
      return
    }

    const portalElement = getOrCreatePortalElement(portalId)
    const currentCount = portalTrapRefCounts.get(portalId) ?? 0

    if (currentCount === 0) {
      portalElement.addEventListener('mousedown', stopMouseDownPropagation)
    }

    portalTrapRefCounts.set(portalId, currentCount + 1)

    return () => {
      const updatedCount = (portalTrapRefCounts.get(portalId) ?? 1) - 1
      portalTrapRefCounts.set(portalId, updatedCount)

      if (updatedCount === 0) {
        portalElement.removeEventListener('mousedown', stopMouseDownPropagation)
        portalTrapRefCounts.delete(portalId)
      }
    }
  }, [portalId])
}

type TextInputProps = ComponentProps<typeof Input>

const CustomTextInput = forwardRef<
  HTMLInputElement,
  TextInputProps & {
    inputRef?: Ref<HTMLInputElement>
    rawValue?: string
    focusOnInit?: boolean
  }
>(({ inputRef, rawValue, value, type, focusOnInit, ...restProps }, ref) => {
  const passRef = useCallback(
    (r: HTMLInputElement) => {
      if (ref != null) {
        if (typeof ref === 'function') {
          ref(r)
        } else {
          // eslint-disable-next-line no-param-reassign
          ;(ref as RefObject<HTMLInputElement>).current = r
        }
      }

      if (inputRef != null) {
        if (typeof inputRef === 'function') {
          inputRef(r)
        } else {
          // eslint-disable-next-line no-param-reassign
          ;(inputRef as RefObject<HTMLInputElement>).current = r
        }
      }
    },
    [inputRef, ref],
  )

  return (
    <Input
      {...restProps} // dont pass "type" into input
      {...{
        ref: passRef,
        value: (value || rawValue) ?? '',
        autoFocus: focusOnInit,
      }}
    />
  )
})

interface DateInputProps
  extends Omit<TextInputProps, 'onChange' | 'ref' | 'value'> {
  inputFormat?: string
  minDate?: Date
  maxDate?: Date
  showYearDropdown?: boolean
  showMonthDropdown?: boolean
  showTimeInput?: boolean
  showTimePicker?: boolean
  dropdownMode?: 'select' | 'scroll'
  onChange?: (utcDate: Date | undefined) => void
  timezone?: string
  /**
   * Date in UTC or string
   *
   * If only date is provided and showTimeInput and showTimePicker
   * are not provided, uses start of a day by default
   */
  value?: Date | string | moment.Moment
  clearable?: boolean
  popperPlacement?: ReactDatePickerProps['popperPlacement']
  popperModifiers?: ReactDatePickerProps['popperModifiers']
  fullWidth?: boolean
  popperProps?: ReactDatePickerProps['popperProps']
  portalId?: ReactDatePickerProps['portalId']
}

export const DATE_INPUT_PICKER_HEIGHT = 285

export const defaultPopperModifiers: ReactDatePickerProps['popperModifiers'] = [
  shift({ padding: 5 }),
  flip({
    fallbackAxisSideDirection: 'start',
    crossAxis: false,
    fallbackPlacements: ['bottom', 'right', 'top', 'left'],
  }),
]

const useOnFocus = ({
  onFocus,
}: {
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void
}) =>
  useHandler((e) => {
    const input = e.target
    if (isIOS) {
      // make sure date picker is visible
      input.scrollIntoView()
    }

    if (typeof onFocus === 'function') {
      onFocus(e)
    }
  })

/**
 * DateInput works only with UTC dates
 *
 * To convert back and forth to clinic timezone, you must set timezone
 *
 * If showTimeInput and showTimePicker are disabled:
 *  - if value is empty, then start of day is always used
 *  - if value contains a time information, then the given time is used
 */
export const DateInput = memo(
  forwardRef<HTMLInputElement, DateInputProps>(
    (
      {
        disabled,
        id,
        inputFormat = PRIMARY_DATE_FORMAT_DATEFNS,
        minDate = new Date(1900, 0, 1),
        maxDate,
        dropdownMode = 'select',
        onChange,
        placeholder = inputFormat,
        showYearDropdown = true,
        showMonthDropdown = true,
        showTimeInput = false,
        showTimePicker = false,
        timezone,
        value,
        onBlur,
        onFocus,
        clearable = false,
        popperPlacement = 'bottom',
        popperModifiers = defaultPopperModifiers,
        fullWidth = false,
        popperProps = { strategy: 'fixed' },
        portalId = DATEPICKER_PORTAL_ID,
        autoFocus,
        ...restInputProps
      },
      ref,
    ) => {
      const { timezone: defaultTimezone } = useDateTimeContext()
      const finalTimezone = timezone ?? defaultTimezone
      if (!finalTimezone) {
        throw new Error(
          'No timezone has been set for DateInput. Please use timezone prop or datetime context',
        )
      }

      const useOnlyDate = !showTimeInput && !showTimePicker
      const onChangeHandler = useCallback(
        (localDate: Date | null) => {
          if (onChange) {
            const r = convertFromLocalDateRepresentation(
              localDate,
              finalTimezone,
              useOnlyDate,
            )

            if (r || (!r && clearable === true)) {
              onChange(r)
            }
          }
        },
        [onChange, finalTimezone, useOnlyDate, clearable],
      )
      const convertedMinDate = useMemoizedCall(convertToTimezone, [
        minDate,
        finalTimezone,
      ])
      const convertedMaxDate = useMemoizedCall(convertToTimezone, [
        maxDate,
        finalTimezone,
      ])
      const selectedDate = useMemoizedCall(convertToLocalDateRepresentation, [
        value,
        finalTimezone,
        useOnlyDate,
      ])
      const rawValue = useMemoizedCall(valueAsIsIfInvalid, [value])
      const handleFocus = useOnFocus({ onFocus })
      usePortalMouseDownTrap(portalId)

      return (
        <>
          <DatePickerPortalStyles />
          <DateInputContainer {...{ fullWidth }}>
            <ReactDatePicker
              {...{
                popperModifiers,
                showPopperArrow: false,
                popperPlacement,
                customInput: (
                  <CustomTextInput
                    {...{
                      autoComplete: 'off',
                      // little ref hack because react-datepicker has terrible ref handling
                      inputRef: ref,
                      placeholder,
                      // if invalid date string is passed, show it as is
                      rawValue,
                      focusOnInit: autoFocus,
                    }}
                    {...restInputProps}
                  />
                ),
                dateFormat: inputFormat,
                disabled,
                dropdownMode,
                id,
                onBlur,
                onFocus: handleFocus,
                showYearDropdown,
                showMonthDropdown,
                showTimeInput,
                showTimePicker,
                minDate: convertedMinDate,
                maxDate: convertedMaxDate,
                onChange: onChangeHandler,
                placeholderText: placeholder,
                openToDate: selectedDate,
                selected: selectedDate,
                strictParsing: true,
                selectsMultiple: undefined,
                selectsRange: undefined,
                portalId,
                popperProps,
              }}
            />
          </DateInputContainer>
        </>
      )
    },
  ),
)
